import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { BookOpen, ExternalLink, MessageCircle, X } from 'lucide-react';
import { getBrainGameUiCopy, getLocalizedBrainGame } from '../../i18n/brainGameLocales';
import ONBOARDING_LOCALES from '../../i18n/onboardingLocales.json';
import { getUFOVLanguageDirection, normalizeUFOVLanguage } from '../../i18n/ufovLocales';
import OnboardingProgress from '../OnboardingProgress';
import { UFOV_ONBOARDING_STATIC_ASSETS } from '../UFOVGameUI';
import {
    BRAIN_GAME_ICON_BY_ID,
    BRAIN_GAME_ICON_IMAGE_BY_ID,
    BrainTrainingGameIcon,
    TutorialModal,
} from './shared';
import { GAME_BY_ID } from './registry';

const TASTE_GAMES = [
    { id: 'chance-cards', gameProps: { roundLimit: 3 } },
    { id: 'stroop-colors', gameProps: { roundLimit: 4 } },
    { id: 'memory-match', gameProps: { pairCount: 4 } },
    { id: 'turn-match', gameProps: { roundLimit: 3 } },
    { id: 'balance-beam', gameProps: { roundLimit: 3 } },
    {
        id: 'dual-track',
        gameProps: {
            runDurationMs: 30000,
            speedMultiplier: 0.62,
        },
    },
];

const TASTE_ICON_IMAGE_BY_ID = {
    ...BRAIN_GAME_ICON_IMAGE_BY_ID,
    'balance-beam': '/assets/ufov/brain-games/icons/number-rain.png',
};

const EVIDENCE_PAPERS = [
    {
        authors: 'Owen et al. | 2010',
        journal: 'Nature',
        title: 'Putting brain training to the test',
    },
    {
        authors: 'Melby-Lervag et al. | 2016',
        journal: 'Perspectives on Psychological Science',
        title: 'Working Memory Training Does Not Improve Far Transfer',
    },
    {
        authors: 'Simons et al. | 2016',
        journal: 'Psychological Science in the Public Interest',
        title: 'Do "Brain-Training" Programs Work?',
    },
];

const SOPT_CLASSIC_STUDIES = [
    {
        meta: 'Ball et al. · JAMA · 2002',
        title: 'Effects of cognitive training interventions with older adults',
        url: 'https://pubmed.ncbi.nlm.nih.gov/12076404/',
    },
    {
        meta: 'Rebok et al. · Journal of the American Geriatrics Society · 2014',
        title: 'Ten-year effects of the ACTIVE cognitive training trial',
        url: 'https://pubmed.ncbi.nlm.nih.gov/24417410/',
    },
    {
        meta: 'Edwards et al. · Alzheimer’s & Dementia · 2017',
        title: 'Speed-of-processing training and ten-year dementia incidence',
        url: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC5700828/',
    },
];

const formatLocalizedCopy = (template, values) => Object.entries(values).reduce(
    (copy, [key, value]) => copy.replaceAll(`{${key}}`, `${value}`),
    `${template || ''}`
);

const getTasteCopy = (language) => {
    const normalizedLanguage = normalizeUFOVLanguage(language);

    return {
        ...ONBOARDING_LOCALES.en.taste,
        ...(ONBOARDING_LOCALES[normalizedLanguage]?.taste || {}),
        statements: {
            ...ONBOARDING_LOCALES.en.taste.statements,
            ...(ONBOARDING_LOCALES[normalizedLanguage]?.taste?.statements || {}),
        },
    };
};

function useOnboardingCopyFit(dependency) {
    const screenRef = useRef(null);

    useLayoutEffect(() => {
        const screen = screenRef.current;

        if (!screen || typeof window === 'undefined') {
            return undefined;
        }

        let frameId = 0;
        const needsSmallerCopy = () => {
            const title = screen.querySelector('[data-onboarding-title]');

            if (!title) {
                return screen.scrollHeight > screen.clientHeight + 2;
            }

            const titleStyle = window.getComputedStyle(title);
            const lineHeight = Number.parseFloat(titleStyle.lineHeight)
                || Number.parseFloat(titleStyle.fontSize) * 1.1;
            const titleLines = Math.ceil(title.getBoundingClientRect().height / Math.max(1, lineHeight));

            return titleLines > 3 || screen.scrollHeight > screen.clientHeight + 2;
        };
        const fitCopy = () => {
            screen.classList.remove('is-copy-compact', 'is-copy-tight');

            if (needsSmallerCopy()) {
                screen.classList.add('is-copy-compact');
            }

            if (needsSmallerCopy()) {
                screen.classList.add('is-copy-tight');
            }
        };
        const scheduleFit = () => {
            window.cancelAnimationFrame(frameId);
            frameId = window.requestAnimationFrame(fitCopy);
        };

        scheduleFit();
        window.addEventListener('resize', scheduleFit);

        return () => {
            window.cancelAnimationFrame(frameId);
            window.removeEventListener('resize', scheduleFit);
        };
    }, [dependency]);

    return screenRef;
}

function TasteIcon({ gameId }) {
    const imageSrc = TASTE_ICON_IMAGE_BY_ID[gameId];

    return imageSrc ? (
        <img alt="" aria-hidden="true" draggable="false" src={imageSrc} />
    ) : (
        <BrainTrainingGameIcon icon={BRAIN_GAME_ICON_BY_ID[gameId]} />
    );
}

function TasteIntroScreen({ language, onStart, progressLabel }) {
    const normalizedLanguage = normalizeUFOVLanguage(language);
    const copy = getTasteCopy(normalizedLanguage);
    const screenRef = useOnboardingCopyFit(`${normalizedLanguage}|${copy.introTitle}|${copy.introDescription}`);

    return (
        <section
            aria-label={copy.introAria}
            className="UFOV-game__screen UFOV-game__screen--onboarding UFOV-onboarding-taste__intro"
            dir={getUFOVLanguageDirection(normalizedLanguage)}
            lang={normalizedLanguage}
            ref={screenRef}
        >
            <div className="UFOV-game__screen-top">
                <OnboardingProgress
                    language={normalizedLanguage}
                    step={progressLabel}
                />
            </div>
            <div className="UFOV-game__screen-main">
                <div className="UFOV-onboarding-taste__intro-main">
                    <div className="UFOV-onboarding-taste__intro-icons" aria-hidden="true">
                        {TASTE_GAMES.map((taste) => (
                            <span key={taste.id}><TasteIcon gameId={taste.id} /></span>
                        ))}
                    </div>
                    <div className="UFOV-game__heading UFOV-onboarding-taste__intro-copy">
                        <h1 className="UFOV-game__title is-compact" data-onboarding-title>{copy.introTitle}</h1>
                        <p className="UFOV-game__subtitle">{copy.introDescription}</p>
                    </div>
                </div>
            </div>
            <div className="UFOV-game__screen-footer">
                <button className="UFOV-game__primary-button" onClick={onStart} type="button">
                    {copy.start}
                </button>
            </div>
        </section>
    );
}

function OnboardingBrainGameTaste({
    colorBlindModeEnabled = false,
    language = 'en',
    onComplete,
    progressLabel = '',
}) {
    const transitionTimerRef = useRef(null);
    const transitionLockedRef = useRef(false);
    const [samplerStarted, setSamplerStarted] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const [dualTrackStarted, setDualTrackStarted] = useState(false);
    const [isAdvancing, setIsAdvancing] = useState(false);
    const [metrics, setMetrics] = useState({});
    const activeTaste = TASTE_GAMES[activeIndex];
    const normalizedLanguage = normalizeUFOVLanguage(language);
    const tasteCopy = useMemo(() => getTasteCopy(normalizedLanguage), [normalizedLanguage]);
    const activeGame = useMemo(
        () => getLocalizedBrainGame(GAME_BY_ID[activeTaste.id], language),
        [activeTaste.id, language]
    );
    const sharedCopy = useMemo(() => getBrainGameUiCopy(language), [language]);
    const ActiveComponent = activeGame.Component;
    const isDualTrack = activeTaste.id === 'dual-track';
    const shouldMountGame = !isDualTrack || dualTrackStarted;
    const timeRemainingMs = Number.isFinite(metrics.timeRemainingMs)
        ? metrics.timeRemainingMs
        : activeTaste.gameProps.runDurationMs || 0;
    const remainingSeconds = Math.max(0, Math.ceil(timeRemainingMs / 1000));

    useEffect(() => () => window.clearTimeout(transitionTimerRef.current), []);

    const handleMetricsChange = useCallback((nextMetrics = {}) => {
        setMetrics((currentMetrics) => (
            JSON.stringify(currentMetrics) === JSON.stringify(nextMetrics)
                ? currentMetrics
                : nextMetrics
        ));
    }, []);

    const handleRunComplete = useCallback(() => {
        if (transitionLockedRef.current) {
            return;
        }

        transitionLockedRef.current = true;
        setIsAdvancing(true);
        transitionTimerRef.current = window.setTimeout(() => {
            if (activeIndex >= TASTE_GAMES.length - 1) {
                onComplete?.();
                return;
            }

            setActiveIndex((index) => index + 1);
            setDualTrackStarted(false);
            setMetrics({});
            setIsAdvancing(false);
            transitionLockedRef.current = false;
        }, isDualTrack ? 700 : 560);
    }, [activeIndex, isDualTrack, onComplete]);

    const screenClassName = [
        'UFOV-brain-games__screen',
        'UFOV-onboarding-taste',
        'is-plain-game',
        isDualTrack ? 'is-dual-track-game' : '',
        colorBlindModeEnabled ? 'is-color-blind-mode' : '',
        isAdvancing ? 'is-advancing' : '',
    ].filter(Boolean).join(' ');

    if (!samplerStarted) {
        return (
            <TasteIntroScreen
                language={normalizedLanguage}
                onStart={() => setSamplerStarted(true)}
                progressLabel={progressLabel}
            />
        );
    }

    return (
        <section
            aria-label={tasteCopy.samplerAria}
            className={screenClassName}
            dir={getUFOVLanguageDirection(normalizedLanguage)}
            lang={normalizedLanguage}
        >
            <header className="UFOV-onboarding-taste__header">
                <div className="UFOV-onboarding-taste__mark">
                    <TasteIcon gameId={activeTaste.id} />
                </div>
                <div
                    aria-label={formatLocalizedCopy(tasteCopy.gameProgress, {
                        current: activeIndex + 1,
                        total: TASTE_GAMES.length,
                    })}
                    aria-valuemax={TASTE_GAMES.length}
                    aria-valuemin="1"
                    aria-valuenow={activeIndex + 1}
                    className="UFOV-onboarding-taste__game-progress"
                    role="progressbar"
                >
                    {TASTE_GAMES.map((taste, index) => (
                        <i
                            className={index <= activeIndex ? 'is-complete' : ''}
                            key={taste.id}
                        />
                    ))}
                </div>
                <span className={`UFOV-onboarding-taste__counter ${isDualTrack && dualTrackStarted ? 'is-timer' : ''}`.trim()}>
                    {isDualTrack && dualTrackStarted
                        ? `${remainingSeconds}s`
                        : `${activeIndex + 1}/${TASTE_GAMES.length}`}
                </span>
            </header>

            {shouldMountGame ? (
                <ActiveComponent
                    {...activeTaste.gameProps}
                    colorBlindModeEnabled={colorBlindModeEnabled}
                    copy={sharedCopy}
                    game={activeGame}
                    hud={activeGame.hud || {}}
                    key={activeTaste.id}
                    onMetricsChange={handleMetricsChange}
                    onRunComplete={handleRunComplete}
                    showResults={false}
                />
            ) : null}

            {isDualTrack && !dualTrackStarted ? (
                <TutorialModal
                    copy={sharedCopy}
                    game={activeGame}
                    onClose={() => setDualTrackStarted(true)}
                />
            ) : null}
        </section>
    );
}

function StudiesSheet({ copy, onClose, open }) {
    const closeButtonRef = useRef(null);
    const sheetRef = useRef(null);

    useEffect(() => {
        if (!open || typeof window === 'undefined') {
            return undefined;
        }

        const previouslyFocused = document.activeElement;
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                onClose?.();
                return;
            }

            if (event.key !== 'Tab') {
                return;
            }

            const focusableElements = Array.from(sheetRef.current?.querySelectorAll(
                'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
            ) || []);
            const firstFocusable = focusableElements[0];
            const lastFocusable = focusableElements[focusableElements.length - 1];

            if (!firstFocusable || !lastFocusable) {
                event.preventDefault();
                sheetRef.current?.focus();
            } else if (event.shiftKey && document.activeElement === firstFocusable) {
                event.preventDefault();
                lastFocusable.focus();
            } else if (!event.shiftKey && document.activeElement === lastFocusable) {
                event.preventDefault();
                firstFocusable.focus();
            }
        };

        closeButtonRef.current?.focus();
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            previouslyFocused?.focus?.();
        };
    }, [onClose, open]);

    if (!open) {
        return null;
    }

    return (
        <div
            className="UFOV-onboarding-studies__backdrop"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                    onClose?.();
                }
            }}
        >
            <section
                aria-labelledby="UFOV-onboarding-studies-title"
                aria-modal="true"
                className="UFOV-onboarding-studies__sheet"
                ref={sheetRef}
                role="dialog"
                tabIndex="-1"
            >
                <div className="UFOV-onboarding-studies__handle" aria-hidden="true" />
                <header className="UFOV-onboarding-studies__header">
                    <div>
                        <span><BookOpen aria-hidden="true" strokeWidth={2} /></span>
                        <h2 id="UFOV-onboarding-studies-title">{copy.studiesTitle}</h2>
                    </div>
                    <button
                        aria-label={copy.closeStudies}
                        className="UFOV-onboarding-studies__close"
                        onClick={onClose}
                        ref={closeButtonRef}
                        type="button"
                    >
                        <X aria-hidden="true" strokeWidth={2.2} />
                    </button>
                </header>
                <p className="UFOV-onboarding-studies__description">{copy.studiesDescription}</p>
                <div className="UFOV-onboarding-studies__list">
                    {SOPT_CLASSIC_STUDIES.map((study) => (
                        <a href={study.url} key={study.url} rel="noreferrer" target="_blank">
                            <span>{study.meta}</span>
                            <strong>{study.title}</strong>
                            <ExternalLink aria-hidden="true" strokeWidth={2} />
                        </a>
                    ))}
                </div>
            </section>
        </div>
    );
}

function StatementVisual({ copy, variant }) {
    if (variant === 'catalog') {
        return (
            <div className="UFOV-onboarding-taste__icon-cloud" aria-hidden="true">
                {TASTE_GAMES.map((taste) => (
                    <span key={taste.id}><TasteIcon gameId={taste.id} /></span>
                ))}
                <b>+22</b>
            </div>
        );
    }

    if (variant === 'belief') {
        return (
            <div className="UFOV-onboarding-taste__belief-mark" aria-hidden="true">
                <span>?</span>
            </div>
        );
    }

    if (variant === 'evidence') {
        return (
            <div className="UFOV-onboarding-taste__paper-stack" aria-hidden="true">
                {EVIDENCE_PAPERS.map((paper) => (
                    <article className="UFOV-onboarding-taste__paper" key={paper.title}>
                        <span>{paper.journal}</span>
                        <strong>{paper.title}</strong>
                        <small>{paper.authors}</small>
                        <i>Abstract</i>
                        <div>
                            <b />
                            <b />
                            <b />
                            <b />
                            <b />
                        </div>
                    </article>
                ))}
            </div>
        );
    }

    if (variant === 'improvement') {
        return (
            <div className="UFOV-onboarding-taste__improvement-mark" aria-hidden="true">
                <img
                    alt=""
                    draggable="false"
                    src="/assets/ufov/onboarding/team-improvement-illustration.png"
                />
            </div>
        );
    }

    if (variant === 'contact') {
        return (
            <div className="UFOV-onboarding-taste__contact-mark" aria-hidden="true">
                <div className="UFOV-game__contact-pill UFOV-onboarding-taste__contact-pill">
                    <MessageCircle className="UFOV-game__contact-pill-icon" strokeWidth={2.2} />
                    <span className="UFOV-game__contact-pill-copy">
                        <strong>{copy.buttonLabel}</strong>
                        <span>{copy.buttonHint}</span>
                    </span>
                </div>
            </div>
        );
    }

    if (variant === 'lifetime') {
        return (
            <div className="UFOV-onboarding-taste__lifetime-mark" aria-hidden="true">
                <img
                    alt=""
                    draggable="false"
                    src={UFOV_ONBOARDING_STATIC_ASSETS.lifetimeAccessIllustration}
                />
            </div>
        );
    }

    return (
        <div className="UFOV-onboarding-taste__sopt-mark" aria-hidden="true">
            <img
                alt=""
                draggable="false"
                src="/assets/ufov/onboarding/sopt-processing-discovery.png"
            />
        </div>
    );
}

function OnboardingTasteStatementScreen({
    language = 'en',
    onAnswer,
    onNext,
    progressLabel,
    variant = 'catalog',
}) {
    const normalizedLanguage = normalizeUFOVLanguage(language);
    const tasteCopy = getTasteCopy(normalizedLanguage);
    const copy = tasteCopy.statements[variant] || tasteCopy.statements.catalog;
    const isQuestion = variant === 'belief';
    const showsStudies = variant === 'exception';
    const [studiesOpen, setStudiesOpen] = useState(false);
    const closeStudies = useCallback(() => setStudiesOpen(false), []);
    const screenRef = useOnboardingCopyFit(`${normalizedLanguage}|${variant}|${copy.title}|${copy.description || ''}`);

    return (
        <>
            <section
                aria-label={copy.ariaLabel}
                className={`UFOV-game__screen UFOV-game__screen--onboarding UFOV-onboarding-taste__statement is-${variant}`}
                dir={getUFOVLanguageDirection(normalizedLanguage)}
                lang={normalizedLanguage}
                ref={screenRef}
            >
                <div className="UFOV-game__screen-top">
                    <OnboardingProgress
                        language={normalizedLanguage}
                        step={progressLabel}
                    />
                </div>
                <div className="UFOV-game__screen-main">
                    <div className="UFOV-onboarding-taste__statement-main">
                        <StatementVisual copy={copy} variant={variant} />
                        <div className="UFOV-game__heading UFOV-onboarding-taste__statement-copy">
                            <h1 className="UFOV-game__title is-compact" data-onboarding-title>{copy.title}</h1>
                            {copy.description ? <p className="UFOV-game__subtitle">{copy.description}</p> : null}
                        </div>
                    </div>
                </div>
                <div className="UFOV-game__screen-footer">
                    {isQuestion ? (
                        <div className="UFOV-onboarding-taste__belief-actions">
                            <button className="UFOV-game__secondary-button" onClick={() => onAnswer?.('yes')} type="button">{tasteCopy.yes}</button>
                            <button className="UFOV-game__secondary-button" onClick={() => onAnswer?.('no')} type="button">{tasteCopy.no}</button>
                        </div>
                    ) : (
                        <div className="UFOV-onboarding-taste__statement-actions">
                            {showsStudies ? (
                                <button
                                    className="UFOV-game__secondary-button UFOV-onboarding-taste__studies-button"
                                    onClick={() => setStudiesOpen(true)}
                                    type="button"
                                >
                                    <BookOpen aria-hidden="true" strokeWidth={2} />
                                    {tasteCopy.viewStudies}
                                </button>
                            ) : null}
                            <button className="UFOV-game__primary-button" onClick={onNext} type="button">
                                {tasteCopy.continue}
                            </button>
                        </div>
                    )}
                </div>
            </section>
            <StudiesSheet copy={tasteCopy} onClose={closeStudies} open={studiesOpen} />
        </>
    );
}

const OnboardingBrainGameTasteStyles = `
    .UFOV-onboarding-taste {
        background: var(--UFOV-color-canvas, #f7f8fc);
        gap: 8px;
        padding:
            max(14px, env(safe-area-inset-top, 0px))
            var(--UFOV-screen-horizontal-gap, 16px)
            max(14px, env(safe-area-inset-bottom, 0px));
        transition: opacity var(--UFOV-interaction-standard, 220ms) var(--UFOV-interaction-ease, ease-out);
    }

    .UFOV-onboarding-taste.is-advancing {
        opacity: 0;
        pointer-events: none;
    }

    .UFOV-onboarding-taste__header {
        align-items: center;
        display: grid;
        gap: 12px;
        grid-template-columns: 42px minmax(0, 1fr) 42px;
        min-height: 44px;
        position: relative;
        z-index: 30;
    }

    .UFOV-onboarding-taste__mark,
    .UFOV-onboarding-taste__counter {
        align-items: center;
        background: #ffffff;
        border: 1px solid rgba(31, 41, 55, 0.08);
        border-radius: 999px;
        box-shadow: 0 9px 20px rgba(31, 41, 55, 0.08);
        display: flex;
        height: 42px;
        justify-content: center;
        width: 42px;
    }

    .UFOV-onboarding-taste__mark img {
        height: 27px;
        object-fit: contain;
        width: 27px;
    }

    .UFOV-onboarding-taste__mark svg {
        height: 20px;
        width: 20px;
    }

    .UFOV-onboarding-taste__counter {
        color: #27324a;
        font-size: 12px;
        line-height: 1;
    }

    .UFOV-onboarding-taste__counter.is-timer {
        font-size: 14px;
    }

    .UFOV-onboarding-taste__game-progress {
        display: grid;
        gap: 5px;
        grid-template-columns: repeat(6, minmax(0, 1fr));
    }

    .UFOV-onboarding-taste__game-progress i {
        background: #dfe3ea;
        border-radius: 999px;
        display: block;
        height: 5px;
        transition: background 240ms ease, transform 240ms ease;
    }

    .UFOV-onboarding-taste__game-progress i.is-complete {
        background: #2f76e8;
        transform: scaleY(1.35);
    }

    .UFOV-onboarding-taste .UFOV-brain-games__play {
        transition: opacity 180ms ease, transform 180ms ease;
    }

    .UFOV-onboarding-taste.is-advancing .UFOV-brain-games__play {
        transform: scale(0.98);
    }

    .UFOV-onboarding-taste .UFOV-brain-games__memory-grid {
        align-self: center;
        grid-template-columns: repeat(4, minmax(0, 1fr));
        max-height: 390px;
        width: min(100%, 370px);
    }

    .UFOV-onboarding-taste__intro {
        background: var(--UFOV-color-canvas, #f7f8fc);
        gap: 0;
        overflow: hidden;
    }

    .UFOV-onboarding-taste__intro .UFOV-game__screen-top {
        min-height: 0;
        padding: 0;
    }

    .UFOV-onboarding-taste__intro .UFOV-game__screen-main {
        align-items: center;
        display: flex;
        justify-content: center;
        min-height: 0;
        padding: clamp(12px, 1.8vh, 18px) 0;
    }

    .UFOV-onboarding-taste__intro-main {
        display: grid;
        gap: 30px;
        justify-items: center;
        width: min(100%, 360px);
    }

    .UFOV-onboarding-taste__intro-icons {
        display: grid;
        gap: 10px;
        grid-template-columns: repeat(3, 58px);
    }

    .UFOV-onboarding-taste__intro-icons > span {
        align-items: center;
        background: #ffffff;
        border: 1px solid rgba(31, 41, 55, 0.07);
        border-radius: 50%;
        box-shadow: 0 10px 22px rgba(31, 41, 55, 0.08);
        display: flex;
        height: 58px;
        justify-content: center;
        width: 58px;
    }

    .UFOV-onboarding-taste__intro-icons img {
        height: 36px;
        object-fit: contain;
        width: 36px;
    }

    .UFOV-onboarding-taste__intro-icons svg {
        height: 26px;
        width: 26px;
    }

    .UFOV-onboarding-taste__intro-copy {
        display: grid;
        gap: 10px;
        max-width: 340px;
        text-align: center;
    }

    .UFOV-onboarding-taste__intro .UFOV-game__screen-footer {
        padding: 0;
    }

    .UFOV-onboarding-taste__statement {
        background: var(--UFOV-color-canvas, #f7f8fc);
        gap: 0;
        overflow: hidden;
    }

    .UFOV-onboarding-taste__statement .UFOV-game__screen-top {
        min-height: 0;
        padding: 0;
    }

    .UFOV-onboarding-taste__statement .UFOV-game__screen-main {
        align-items: center;
        display: flex;
        justify-content: center;
        min-height: 0;
        padding: clamp(12px, 1.8vh, 18px) 0;
    }

    .UFOV-onboarding-taste__statement-main {
        align-items: center;
        display: grid;
        gap: 30px;
        justify-items: center;
        width: 100%;
    }

    .UFOV-onboarding-taste__statement-copy {
        display: grid;
        gap: 10px;
        max-width: 340px;
        text-align: center;
    }

    .UFOV-onboarding-taste__statement.is-evidence .UFOV-onboarding-taste__statement-main {
        gap: 20px;
    }

    .UFOV-onboarding-taste__statement .UFOV-game__screen-footer {
        padding: 0;
    }

    .UFOV-onboarding-taste__statement-actions {
        display: grid;
        gap: 10px;
        width: 100%;
    }

    .UFOV-onboarding-taste__studies-button {
        gap: 8px;
    }

    .UFOV-onboarding-taste__studies-button svg {
        height: 19px;
        width: 19px;
    }

    .UFOV-onboarding-studies__backdrop {
        align-items: flex-end;
        animation: UFOV-onboarding-studies-backdrop-in var(--UFOV-interaction-standard, 220ms) var(--UFOV-interaction-ease, ease-out) both;
        background: rgba(13, 22, 39, 0.46);
        display: flex;
        inset: 0;
        justify-content: center;
        position: fixed;
        z-index: 15000;
    }

    .UFOV-onboarding-studies__sheet {
        animation: UFOV-onboarding-studies-sheet-in var(--UFOV-interaction-standard, 220ms) var(--UFOV-interaction-ease, ease-out) both;
        background: var(--UFOV-color-surface-strong, #fff);
        border: 1px solid var(--UFOV-color-border, rgba(19, 38, 79, 0.12));
        border-radius: var(--UFOV-radius-sheet, 28px) var(--UFOV-radius-sheet, 28px) 0 0;
        box-shadow: 0 -18px 50px rgba(13, 22, 39, 0.18);
        color: var(--UFOV-color-text, #13264f);
        display: grid;
        gap: 14px;
        max-height: min(78vh, 680px);
        max-width: 430px;
        overflow-y: auto;
        padding: 10px 18px max(22px, calc(env(safe-area-inset-bottom, 0px) + 14px));
        width: 100%;
    }

    .UFOV-onboarding-studies__handle {
        background: var(--UFOV-color-track, #e6e9f2);
        border-radius: var(--UFOV-radius-pill, 999px);
        height: 5px;
        justify-self: center;
        width: 42px;
    }

    .UFOV-onboarding-studies__header {
        align-items: center;
        display: flex;
        gap: 12px;
        justify-content: space-between;
    }

    .UFOV-onboarding-studies__header > div {
        align-items: center;
        display: flex;
        gap: 10px;
        min-width: 0;
    }

    .UFOV-onboarding-studies__header > div > span {
        align-items: center;
        background: var(--UFOV-color-action-soft, #eef4ff);
        border-radius: var(--UFOV-radius-md, 12px);
        color: var(--UFOV-color-action, #1f6bff);
        display: inline-flex;
        flex: 0 0 auto;
        height: 40px;
        justify-content: center;
        width: 40px;
    }

    .UFOV-onboarding-studies__header svg {
        height: 20px;
        width: 20px;
    }

    .UFOV-onboarding-studies__header h2 {
        color: var(--UFOV-color-heading, #0d1627);
        font-size: 20px;
        font-weight: var(--UFOV-font-weight-bold, 700);
        line-height: 1.15;
        margin: 0;
    }

    .UFOV-onboarding-studies__close {
        align-items: center;
        background: var(--UFOV-color-action-soft, #eef4ff);
        border: 1px solid var(--UFOV-color-action-border, #cfddf8);
        border-radius: var(--UFOV-radius-pill, 999px);
        color: var(--UFOV-color-text, #13264f);
        cursor: pointer;
        display: inline-flex;
        flex: 0 0 auto;
        height: 44px;
        justify-content: center;
        transition: transform var(--UFOV-interaction-fast, 160ms) var(--UFOV-interaction-ease, ease-out);
        width: 44px;
    }

    .UFOV-onboarding-studies__close:active {
        transform: scale(var(--UFOV-interaction-press-scale, 0.98));
    }

    .UFOV-onboarding-studies__close:focus-visible,
    .UFOV-onboarding-studies__list a:focus-visible {
        outline: 3px solid var(--UFOV-color-focus-ring, rgba(31, 107, 255, 0.38));
        outline-offset: 2px;
    }

    .UFOV-onboarding-studies__description {
        color: var(--UFOV-color-text-muted, #697487);
        font-size: 14px;
        font-weight: var(--UFOV-font-weight-regular, 400);
        line-height: 1.45;
        margin: 0;
    }

    .UFOV-onboarding-studies__list {
        display: grid;
        gap: 10px;
    }

    .UFOV-onboarding-studies__list a {
        align-items: center;
        background: var(--UFOV-color-surface-strong, #fff);
        border: 1px solid var(--UFOV-color-border, rgba(19, 38, 79, 0.12));
        border-radius: var(--UFOV-radius-lg, 16px);
        color: var(--UFOV-color-text, #13264f);
        display: grid;
        gap: 4px 12px;
        grid-template-columns: minmax(0, 1fr) 20px;
        padding: 14px;
        text-decoration: none;
        transition:
            border-color var(--UFOV-interaction-fast, 160ms) var(--UFOV-interaction-ease, ease-out),
            transform var(--UFOV-interaction-fast, 160ms) var(--UFOV-interaction-ease, ease-out);
    }

    .UFOV-onboarding-studies__list a:hover {
        border-color: var(--UFOV-color-action, #1f6bff);
    }

    .UFOV-onboarding-studies__list a:active {
        transform: scale(var(--UFOV-interaction-press-scale, 0.98));
    }

    .UFOV-onboarding-studies__list a span,
    .UFOV-onboarding-studies__list a strong {
        grid-column: 1;
    }

    .UFOV-onboarding-studies__list a span {
        color: var(--UFOV-color-text-muted, #697487);
        font-size: 12px;
        font-weight: var(--UFOV-font-weight-medium, 500);
        line-height: 1.3;
    }

    .UFOV-onboarding-studies__list a strong {
        font-size: 14px;
        font-weight: var(--UFOV-font-weight-semibold, 600);
        line-height: 1.35;
    }

    .UFOV-onboarding-studies__list a > svg {
        color: var(--UFOV-color-action, #1f6bff);
        grid-column: 2;
        grid-row: 1 / span 2;
        height: 18px;
        width: 18px;
    }

    @keyframes UFOV-onboarding-studies-backdrop-in {
        from { opacity: 0; }
        to { opacity: 1; }
    }

    @keyframes UFOV-onboarding-studies-sheet-in {
        from { transform: translateY(24px); }
        to { transform: translateY(0); }
    }

    .UFOV-onboarding-taste__icon-cloud {
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
        justify-content: center;
        width: 262px;
    }

    .UFOV-onboarding-taste__icon-cloud > span,
    .UFOV-onboarding-taste__icon-cloud > b {
        align-items: center;
        background: #ffffff;
        border: 1px solid rgba(31, 41, 55, 0.07);
        border-radius: 50%;
        box-shadow: 0 10px 20px rgba(31, 41, 55, 0.08);
        display: flex;
        height: 58px;
        justify-content: center;
        width: 58px;
    }

    .UFOV-onboarding-taste__icon-cloud img {
        height: 38px;
        object-fit: contain;
        width: 38px;
    }

    .UFOV-onboarding-taste__icon-cloud svg {
        height: 26px;
        width: 26px;
    }

    .UFOV-onboarding-taste__icon-cloud > b {
        color: #2f76e8;
        font-size: 17px;
        font-weight: 600;
    }

    .UFOV-onboarding-taste__belief-mark {
        align-items: center;
        background: #ffffff;
        border: 1px solid rgba(31, 41, 55, 0.07);
        border-radius: 50%;
        box-shadow: 0 18px 42px rgba(31, 41, 55, 0.1);
        display: flex;
        height: 150px;
        justify-content: center;
        width: 150px;
    }

    .UFOV-onboarding-taste__belief-mark span {
        color: #2f76e8;
        font-size: 86px;
        font-weight: 600;
        line-height: 1;
    }

    .UFOV-onboarding-taste__belief-actions {
        display: grid;
        gap: 12px;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        width: 100%;
    }

    .UFOV-onboarding-taste__belief-actions button {
        background: var(--UFOV-color-surface-strong, #fff);
        border: 1px solid var(--UFOV-color-border, rgba(19, 38, 79, 0.12));
        border-radius: var(--UFOV-radius-xl, 22px);
        box-shadow: 0 7px 16px rgba(31, 41, 55, 0.07);
        color: var(--UFOV-color-heading, #0d1627);
        cursor: pointer;
        font: inherit;
        font-size: 16px;
        font-weight: var(--UFOV-font-weight-semibold, 600);
        height: 58px;
        min-height: 58px;
        transition:
            background-color var(--UFOV-interaction-fast, 160ms) var(--UFOV-interaction-ease, ease-out),
            border-color var(--UFOV-interaction-fast, 160ms) var(--UFOV-interaction-ease, ease-out),
            color var(--UFOV-interaction-fast, 160ms) var(--UFOV-interaction-ease, ease-out),
            transform var(--UFOV-interaction-fast, 160ms) var(--UFOV-interaction-ease, ease-out);
    }

    .UFOV-onboarding-taste__belief-actions button:hover {
        border-color: var(--UFOV-color-action, #1f6bff);
    }

    .UFOV-onboarding-taste__belief-actions button:active {
        background: var(--UFOV-color-action, #1f6bff);
        border-color: var(--UFOV-color-action, #1f6bff);
        color: #ffffff;
        transform: scale(var(--UFOV-interaction-press-scale, 0.98));
    }

    .UFOV-onboarding-taste__paper-stack {
        height: 206px;
        position: relative;
        width: min(270px, 100%);
    }

    .UFOV-onboarding-taste__paper {
        background: #ffffff;
        border: 1px solid #e1e4e9;
        border-radius: 5px;
        box-shadow: 0 13px 26px rgba(31, 41, 55, 0.16);
        box-sizing: border-box;
        display: grid;
        gap: 7px;
        height: 186px;
        left: 50%;
        overflow: hidden;
        padding: 15px 13px;
        position: absolute;
        top: 10px;
        transform-origin: 50% 86%;
        width: 142px;
    }

    .UFOV-onboarding-taste__paper:nth-child(1) {
        transform: translateX(-91%) rotate(-9deg);
    }

    .UFOV-onboarding-taste__paper:nth-child(2) {
        transform: translateX(-9%) rotate(9deg);
    }

    .UFOV-onboarding-taste__paper:nth-child(3) {
        top: 0;
        transform: translateX(-50%) rotate(-1deg);
    }

    .UFOV-onboarding-taste__paper > span {
        color: #7a8496;
        font-size: 7px;
        font-style: normal;
        font-weight: 600;
        line-height: 1.15;
        min-height: 16px;
        text-transform: uppercase;
    }

    .UFOV-onboarding-taste__paper > strong {
        color: #17233b;
        font-size: 10px;
        font-weight: 600;
        line-height: 1.18;
    }

    .UFOV-onboarding-taste__paper > small {
        color: #687287;
        font-size: 7px;
        font-weight: 500;
    }

    .UFOV-onboarding-taste__paper > i {
        border-top: 1px solid #dce1e8;
        color: #8790a0;
        font-size: 7px;
        font-style: normal;
        font-weight: 600;
        padding-top: 7px;
        text-transform: uppercase;
    }

    .UFOV-onboarding-taste__paper > div {
        display: grid;
        gap: 5px;
    }

    .UFOV-onboarding-taste__paper > div b {
        background: #e6e9ee;
        border-radius: 999px;
        display: block;
        height: 3px;
        width: 100%;
    }

    .UFOV-onboarding-taste__paper > div b:nth-child(2) {
        width: 88%;
    }

    .UFOV-onboarding-taste__paper > div b:nth-child(4) {
        width: 94%;
    }

    .UFOV-onboarding-taste__paper > div b:nth-child(5) {
        width: 68%;
    }

    .UFOV-onboarding-taste__sopt-mark {
        align-items: center;
        display: flex;
        height: 210px;
        justify-content: center;
        position: relative;
        width: 240px;
    }

    .UFOV-onboarding-taste__sopt-mark img {
        height: 210px;
        object-fit: contain;
        width: 240px;
    }

    .UFOV-onboarding-taste__improvement-mark {
        align-items: center;
        display: flex;
        height: 238px;
        justify-content: center;
        width: min(330px, 100%);
    }

    .UFOV-onboarding-taste__improvement-mark img {
        display: block;
        height: 238px;
        object-fit: contain;
        width: 100%;
    }

    .UFOV-onboarding-taste__contact-mark {
        align-items: center;
        display: flex;
        height: 170px;
        justify-content: center;
        width: 100%;
    }

    .UFOV-onboarding-taste__contact-pill {
        grid-column: auto;
        justify-self: auto;
        min-height: 56px;
        padding: 8px 18px;
        pointer-events: none;
        transform: scale(1.45);
    }

    .UFOV-onboarding-taste__contact-pill .UFOV-game__contact-pill-icon {
        height: 18px;
        width: 18px;
    }

    .UFOV-onboarding-taste__contact-pill .UFOV-game__contact-pill-copy strong {
        font-size: 12px;
        line-height: 14px;
    }

    .UFOV-onboarding-taste__contact-pill .UFOV-game__contact-pill-copy > span {
        font-size: 9px;
        line-height: 11px;
    }

    .UFOV-onboarding-taste__lifetime-mark {
        align-items: center;
        display: flex;
        height: 210px;
        justify-content: center;
        width: 100%;
    }

    .UFOV-onboarding-taste__lifetime-mark img {
        display: block;
        height: 210px;
        object-fit: contain;
        width: 210px;
    }

    @media (max-height: 660px) {
        .UFOV-onboarding-taste__intro-main {
            gap: 20px;
        }

        .UFOV-onboarding-taste__statement-main {
            gap: 20px;
        }

        .UFOV-onboarding-taste__paper-stack {
            height: 172px;
            transform: scale(0.84);
            transform-origin: center;
        }

        .UFOV-onboarding-taste__sopt-mark {
            height: 160px;
            width: 190px;
        }

        .UFOV-onboarding-taste__sopt-mark img {
            height: 160px;
            width: 190px;
        }

        .UFOV-onboarding-taste__improvement-mark,
        .UFOV-onboarding-taste__improvement-mark img {
            height: 188px;
        }

        .UFOV-onboarding-taste__contact-mark {
            height: 130px;
        }

        .UFOV-onboarding-taste__lifetime-mark {
            height: 160px;
        }

        .UFOV-onboarding-taste__lifetime-mark img {
            height: 160px;
            width: 160px;
        }
    }

    .UFOV-onboarding-taste__intro .UFOV-game__title,
    .UFOV-onboarding-taste__intro .UFOV-game__subtitle,
    .UFOV-onboarding-taste__statement .UFOV-game__title,
    .UFOV-onboarding-taste__statement .UFOV-game__subtitle {
        hyphens: none;
        overflow-wrap: normal;
        word-break: normal;
    }
`;

export {
    OnboardingBrainGameTaste,
    OnboardingBrainGameTasteStyles,
    OnboardingTasteStatementScreen,
};
